// Shared form used by both Create + Edit facility pages.

import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Grid2 as Grid,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useT } from '../../i18n';
import type { FacilityType, PublicFacility } from '../../lib/facilities-api';
import { uploadOrDataUrl } from '../../lib/files-api';

// Facility cover image cap — checked client-side so an oversized file never
// becomes a multi-MB data URL (the R2-absent dev fallback) or hits the server.
const MAX_IMAGE_BYTES = 3 * 1024 * 1024;
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const TYPE_OPTIONS: ReadonlyArray<{ value: FacilityType; labelKey: string }> = [
  { value: 'PADEL', labelKey: 'fac.typePadel' },
  { value: 'TENNIS', labelKey: 'fac.typeTennis' },
  { value: 'BASKETBALL', labelKey: 'fac.typeBasketball' },
  { value: 'GYM', labelKey: 'fac.typeGym' },
  { value: 'POOL', labelKey: 'fac.typePool' },
  { value: 'MEETING_ROOM', labelKey: 'fac.typeMeeting' },
  { value: 'BBQ_AREA', labelKey: 'fac.typeBbq' },
  { value: 'OTHER', labelKey: 'fac.typeOther' },
];

export interface FacilityFormValues {
  name: string;
  type: FacilityType;
  description: string;
  capacity: string;            // text → parsed to number on submit
  open_time: string;           // HH:MM or ''
  close_time: string;
  requires_booking: boolean;   // false = walk-in (no slots/approval/bookings)
  slot_minutes: string;
  requires_approval: boolean;
  image_url: string;
  is_active: boolean;
}

export function blankForm(): FacilityFormValues {
  return {
    name: '',
    type: 'OTHER',
    description: '',
    capacity: '',
    open_time: '',
    close_time: '',
    requires_booking: true,
    slot_minutes: '60',
    requires_approval: false,
    image_url: '',
    is_active: true,
  };
}

export function formFromFacility(f: PublicFacility): FacilityFormValues {
  return {
    name: f.name,
    type: f.type,
    description: f.description ?? '',
    capacity: f.capacity != null ? String(f.capacity) : '',
    open_time: f.open_time ?? '',
    close_time: f.close_time ?? '',
    requires_booking: f.requires_booking,
    slot_minutes: String(f.slot_minutes),
    requires_approval: f.requires_approval,
    image_url: f.image_url ?? '',
    is_active: f.is_active,
  };
}

export interface FacilityFormProps {
  initial?: PublicFacility;
  submitLabel: string;
  submitting: boolean;
  isEdit?: boolean;
  onSubmit: (values: FacilityFormValues) => Promise<void>;
}

export function FacilityForm({ initial, submitLabel, submitting, isEdit, onSubmit }: FacilityFormProps) {
  const t = useT();
  const [form, setForm] = useState<FacilityFormValues>(
    initial ? formFromFacility(initial) : blankForm(),
  );
  const [error, setError] = useState<string | null>(null);
  const [imageBusy, setImageBusy] = useState(false);

  // If `initial` arrives after first render (async load on edit), reset form.
  useEffect(() => {
    if (initial) setForm(formFromFacility(initial));
  }, [initial]);

  function update<K extends keyof FacilityFormValues>(key: K, value: FacilityFormValues[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function onPickImage(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type && !IMAGE_TYPES.includes(file.type)) {
      setError(t('fac.imageBadType'));
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setError(t('fac.imageTooBig'));
      return;
    }
    setImageBusy(true);
    setError(null);
    try {
      update('image_url', await uploadOrDataUrl(file));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('fac.saveFailed'));
    } finally {
      setImageBusy(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const name = form.name.trim();
    if (name.length === 0) {
      setError(t('fac.validName'));
      return;
    }
    // Slot length only matters for bookable facilities; a walk-in has no slots.
    if (form.requires_booking) {
      const slot = Number(form.slot_minutes);
      if (!Number.isFinite(slot) || slot < 5) {
        setError(t('fac.validSlot'));
        return;
      }
    }
    if ((form.open_time && !form.close_time) || (!form.open_time && form.close_time)) {
      setError(t('fac.validTimes'));
      return;
    }
    const capacity = form.capacity.trim() === '' ? undefined : Number(form.capacity);
    if (capacity !== undefined && (!Number.isFinite(capacity) || capacity < 1)) {
      setError(t('fac.validCapacity'));
      return;
    }
    try {
      await onSubmit(form);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('fac.saveFailed'));
    }
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Box component="form" onSubmit={handleSubmit}>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                label={t('fac.fieldName')}
                required
                fullWidth
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
                inputProps={{ maxLength: 100 }}
                helperText={t('fac.nameHelper')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                select
                label={t('fac.fieldType')}
                fullWidth
                value={form.type}
                onChange={(e) => update('type', e.target.value as FacilityType)}
              >
                {TYPE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {t(o.labelKey)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={12}>
              <TextField
                label={t('fac.fieldDesc')}
                fullWidth
                multiline
                minRows={2}
                value={form.description}
                onChange={(e) => update('description', e.target.value)}
                inputProps={{ maxLength: 1000 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label={t('fac.fieldCapacity')}
                fullWidth
                type="number"
                value={form.capacity}
                onChange={(e) => update('capacity', e.target.value)}
                inputProps={{ min: 1, step: 1 }}
                helperText={t('fac.capacityHelper')}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
              <TextField
                label={t('fac.fieldOpenTime')}
                fullWidth
                type="time"
                value={form.open_time}
                onChange={(e) => update('open_time', e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText={t('fac.timeHelper')}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
              <TextField
                label={t('fac.fieldCloseTime')}
                fullWidth
                type="time"
                value={form.close_time}
                onChange={(e) => update('close_time', e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText={t('fac.timeHelper')}
              />
            </Grid>
            <Grid size={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.requires_booking}
                    onChange={(e) => update('requires_booking', e.target.checked)}
                  />
                }
                label={t('fac.requiresBooking')}
              />
              <Typography variant="caption" display="block" color="text.secondary">
                {t('fac.bookingHelper')}
              </Typography>
            </Grid>
            {form.requires_booking ? (
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label={t('fac.fieldSlot')}
                  fullWidth
                  type="number"
                  value={form.slot_minutes}
                  onChange={(e) => update('slot_minutes', e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">min</InputAdornment>,
                  }}
                  inputProps={{ min: 5, step: 5 }}
                />
              </Grid>
            ) : null}
            {form.requires_booking ? (
              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.requires_approval}
                      onChange={(e) => update('requires_approval', e.target.checked)}
                    />
                  }
                  label={t('fac.requiresApproval')}
                />
                <Typography variant="caption" display="block" color="text.secondary">
                  {t('fac.approvalHelper')}
                </Typography>
              </Grid>
            ) : null}
            <Grid size={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('fac.fieldImage')}
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                {form.image_url ? (
                  <Box
                    component="img"
                    src={form.image_url}
                    alt=""
                    sx={{
                      width: 96,
                      height: 64,
                      objectFit: 'cover',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                ) : null}
                <Button
                  variant="outlined"
                  component="label"
                  disabled={imageBusy}
                  startIcon={imageBusy ? <CircularProgress size={16} /> : undefined}
                >
                  {form.image_url ? t('fac.imageReplace') : t('fac.imageUpload')}
                  <input
                    hidden
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={onPickImage}
                  />
                </Button>
                {form.image_url ? (
                  <Button
                    color="inherit"
                    onClick={() => update('image_url', '')}
                    disabled={imageBusy}
                  >
                    {t('fac.imageRemove')}
                  </Button>
                ) : null}
              </Stack>
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {t('fac.imageHelper')}
              </Typography>
            </Grid>
            {isEdit ? (
              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.is_active}
                      onChange={(e) => update('is_active', e.target.checked)}
                    />
                  }
                  label={t('fac.isActive')}
                />
              </Grid>
            ) : null}
            <Grid size={12}>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button component={RouterLink} to="/facilities" disabled={submitting}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" variant="contained" disabled={submitting}>
                  {submitting ? t('common.saving') : submitLabel}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
}
